if (!customElements.get("product-card")) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      // Pas de Shadow DOM - utilisation du DOM light
      this.swiper = null;
      this.mobileBreakpoint = 768; // Breakpoint mobile en px
      this.resizeTimeout = null;
      this.favButton = this.querySelector(".fav");
      this.attachEventListeners();
    }

    connectedCallback() {
      this.initImageSwiper();
      // Écouter les changements de taille d'écran
      this.handleResize = this.handleResize.bind(this);
      window.addEventListener("resize", this.handleResize);

      this.checkFavorites();
    }

    checkFavorites() {
      const favorites = Favorites.getAll();
      if (favorites.includes(this.dataset.productHandle)) {
        this.favButton.classList.add("selected");
      }
    }

    disconnectedCallback() {
      // Nettoyer le swiper et les listeners
      this.destroySwiper();
      window.removeEventListener("resize", this.handleResize);
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
    }

    static get observedAttributes() {
      return ["vehicle-id", "data-href"];
    }

    /**
     * Vérifie si on est sur mobile
     */
    isMobile() {
      return window.innerWidth < this.mobileBreakpoint;
    }

    /**
     * Gère le redimensionnement de la fenêtre
     */
    handleResize() {
      // Debounce pour éviter trop d'appels
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        // Si on est sur mobile avec no_mobile_swiper et qu'un swiper existe, le détruire
        if (this.isMobile() && this.classList.contains("no_mobile_swiper") && this.swiper) {
          this.destroySwiper();
        }
        // Si on n'est pas sur mobile ou qu'on n'a pas no_mobile_swiper et qu'aucun swiper n'existe, l'initialiser
        else if ((!this.isMobile() || !this.classList.contains("no_mobile_swiper")) && !this.swiper) {
          this.initImageSwiper();
        }
      }, 250);
    }

    /**
     * Initialise le swiper pour les images de la carte
     */
    initImageSwiper() {
      const swiperElement = this.querySelector(".vehicle_image_swiper");

      if (!swiperElement || this.swiper) {
        return;
      }

      // Ne pas initialiser le swiper sur mobile si la classe no_mobile_swiper est présente
      if (this.isMobile() && this.classList.contains("no_mobile_swiper")) {
        return;
      }

      // Vérifier que Swiper est disponible
      if (typeof window.Swiper === "undefined") {
        return;
      }

      try {
        this.swiper = new window.Swiper(swiperElement, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: true,
          pagination: {
            el: this.querySelector(".vehicle-swiper-pagination"),
            clickable: true,
          },
          navigation: {
            nextEl: this.querySelector(".vehicle-swiper-next"),
            prevEl: this.querySelector(".vehicle-swiper-prev"),
          },
          on: {
            slideChangeTransitionStart: () => {
              // Empêcher la propagation du clic vers la carte pendant les transitions
              this.classList.add("swiper-transitioning");
            },
            slideChangeTransitionEnd: () => {
              this.classList.remove("swiper-transitioning");
              // Gestion de l'événement interaction_gallery
              pushDataLayer({ event: "interaction_gallery" });
            },
          },
        });
      } catch (error) {
        console.error("Error initializing vehicle image swiper:", error);
      }
    }

    /**
     * Détruit le swiper
     */
    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
        this.classList.remove("swiper-transitioning");
      }
    }

    /**
     * Attache les event listeners aux éléments de la carte
     */
    attachEventListeners() {
      // Gestion des favoris et navigation
      this.addEventListener("click", (e) => {
        const productHandle = this.dataset.productHandle;
        RecentlySeen.add(productHandle);
        const swiperNavigation = e.target.closest(".vehicle-swiper-prev, .vehicle-swiper-next, .vehicle-swiper-pagination");
        const href = this.getAttribute("data-href");

        // Si le click est sur la navigation du swiper (et qu'on a un swiper actif), on ne navigue pas
        if (swiperNavigation && this.swiper) {
          e.stopPropagation();
          return;
        }

        // Si on est en transition de swiper, on ne navigue pas
        if (this.classList.contains("swiper-transitioning")) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Tracking GTM : select_item sur clic de navigation (les clics swiper /
        // transition ont déjà fait un return ; le cœur favori stoppe la propagation).
        if (!e.target.closest(".compare_wrapper") && typeof forgeVehicleItem === "function") {
          try {
            const json = getCardVehicleJson(this);
            if (json) {
              const listWrapper = this.closest("[data-list-name]");
              const listName = listWrapper ? listWrapper.getAttribute("data-list-name") : "";
              let index = 1;
              if (listWrapper) {
                const cards = Array.prototype.slice.call(listWrapper.querySelectorAll(".product_card"));
                index = cards.indexOf(this) + 1 || 1;
              }
              pushDataLayer({
                event: "select_item",
                value: json.price,
                item_list_name: listName,
                ecommerce: { items: [forgeVehicleItem(json, { index: index, item_list_name: listName })] },
              });
            }
          } catch (err) {}
        }

        // Sinon, on suit le lien data-href
        if (href) {
          window.location.href = href;
        }
      });

      // Empêcher la navigation lors du swipe (seulement si swiper actif)
      this.addEventListener("touchstart", (e) => {
        if (this.swiper && e.target.closest(".vehicle_image_swiper")) {
          this.touchStarted = true;
        }
      });

      this.addEventListener("touchend", (e) => {
        if (this.touchStarted && this.swiper) {
          this.touchStarted = false;
          // Petite temporisation pour éviter le clic après swipe
          setTimeout(() => {
            this.classList.remove("swiper-transitioning");
          }, 100);
        }
      });
    }
  }

  customElements.define("product-card", ProductCard);
}

const RecentlySeen = {
  storageKey: "recently_seen_product_handles",

  getAll() {
    const stored = localStorage.getItem(this.storageKey);
    return stored ? JSON.parse(stored) : [];
  },

  // Get recently seen products count
  getCount() {
    return this.getAll().length;
  },

  // Add a product to recently seen products list
  add(productHandle) {
    const recentlySeen = this.getAll();
    const maxRecentlySeen = 4;
    if (!recentlySeen.includes(productHandle)) {
      recentlySeen.unshift(productHandle);
      localStorage.setItem(this.storageKey, JSON.stringify(recentlySeen));
    }

    if (recentlySeen.length > maxRecentlySeen) {
      recentlySeen.pop();
      localStorage.setItem(this.storageKey, JSON.stringify(recentlySeen));
    }
    return recentlySeen;
  },
};
